import { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { track } from '../../utils/track';
import { useOfferSlug } from './giveawayOfferPages';
import {
  PAGE_BG,
  INK,
  MUTED,
  LINE,
  kickerSx,
  ctaSx
} from './giveawayOfferTheme';

/* "Want more entries?" — the confirmation page's pointer to its packs.
 *
 * WHEN. Only once the packs have loaded and there is at least one to point at
 * (no packs section, no popup), and about 1.5 s after that, so the headline is
 * read first. Never over the checkout: a visitor who opens one before this fires
 * has found the packs unaided, and it is dropped for the visit rather than shown
 * when that checkout closes.
 *
 * ONCE PER ARRIVAL. The sessionStorage flag holds React Router's key for the
 * history entry it was shown on. Every sign-up navigates here with a new key, so
 * it shows again — a tester, or a shared tablet at an event stand, signs up
 * many times in one tab — while a reload, or back/forward to the same entry,
 * keeps the key and does not. A direct load's key is "default". Written when it
 * is SHOWN; storage that throws is swallowed, so at worst a private window sees
 * it again on a reload.
 *
 * NOT ON THE WAY BACK FROM STRIPE. A URL carrying Stripe's return parameters
 * belongs to somebody coming back from paying.
 *
 * WHAT IT MAY SAY. "Up to N more" is passed in, and is the most any plan on the
 * page starts a buyer with (`largestStartingBonus`) — the figure the cards print
 * — never a constant, so it cannot promise more than they do. Like the page
 * under it, nothing here may say the free entries are already counted.
 */

const SHOWN_KEY = 'giveawayOfferMoreEntriesShown';
const DELAY_MS = 1500;
const STRIPE_RETURN_PARAMS = ['redirect_status', 'setup_intent', 'payment_intent'];

const shownForArrival = (arrival: string) => {
  try {
    return sessionStorage.getItem(SHOWN_KEY) === arrival;
  } catch {
    return false;
  }
};

const rememberShown = (arrival: string) => {
  try {
    sessionStorage.setItem(SHOWN_KEY, arrival);
  } catch {
    /* Private browsing, or a full quota: it may show again on a reload. */
  }
};

type Dismissal = 'close' | 'escape' | 'backdrop';

const GiveawayOfferMoreEntriesDialog = ({
  upTo,
  checkoutOpen,
  onSeePacks
}: {
  /** The most any plan on the page starts a buyer with; 0 offers nothing. */
  upTo: number;
  /** Whether the checkout dialog is open. */
  checkoutOpen: boolean;
  /** Takes the visitor to the packs, once this dialog has fully closed. */
  onSeePacks: () => void;
}) => {
  const offerSlug = useOfferSlug();
  const location = useLocation();
  /* This history entry's key — see ONCE PER ARRIVAL above. */
  const arrival = location.key;
  const backFromStripe = STRIPE_RETURN_PARAMS.some((p) =>
    new URLSearchParams(location.search).has(p)
  );
  const [open, setOpen] = useState(false);
  const foundPacksFirst = useRef(false);
  const accepted = useRef(false);

  useEffect(() => {
    if (checkoutOpen) {
      foundPacksFirst.current = true;
      return undefined;
    }
    if (
      upTo <= 0 ||
      foundPacksFirst.current ||
      backFromStripe ||
      shownForArrival(arrival)
    ) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      rememberShown(arrival);
      setOpen(true);
      track('giveaway_offer.more_entries_shown', { offerSlug, upTo });
    }, DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [upTo, checkoutOpen, offerSlug, arrival, backFromStripe]);

  const dismiss = (how: Dismissal) => {
    setOpen(false);
    track('giveaway_offer.more_entries_dismissed', { offerSlug, how });
  };

  const seePacks = () => {
    accepted.current = true;
    setOpen(false);
    track('giveaway_offer.more_entries_accepted', { offerSlug });
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) =>
        dismiss(reason === 'backdropClick' ? 'backdrop' : 'escape')
      }
      aria-labelledby="giveaway-offer-more-entries-title"
      aria-describedby="giveaway-offer-more-entries-headline"
      TransitionProps={{
        /* The trip to the packs waits until the dialog has gone: closing hands
           focus back to wherever it was before, and a focus() that scrolls
           would undo a scroll started any earlier. */
        onExited: () => {
          if (!accepted.current) return;
          accepted.current = false;
          onSeePacks();
        }
      }}
      PaperProps={{
        sx: {
          /* A small card, never full-screen — not even on a phone. */
          m: 2,
          width: 'calc(100% - 32px)',
          maxWidth: 440,
          maxHeight: 'calc(100% - 32px)',
          borderRadius: '18px',
          background: PAGE_BG,
          border: `1px solid ${LINE}`,
          boxShadow: '0 24px 64px rgba(0,0,0,0.55)'
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          px: { xs: 3, sm: 4 },
          pt: { xs: 4, sm: 4.5 },
          pb: { xs: 3.5, sm: 4 },
          textAlign: 'center'
        }}
      >
        {/* This, Escape and a click outside are the only ways out, so it gets
            a full 44px touch target. */}
        <IconButton
          aria-label="Close"
          onClick={() => dismiss('close')}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 44,
            height: 44,
            color: MUTED,
            '&:hover': { color: INK }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          id="giveaway-offer-more-entries-title"
          component="h2"
          sx={{ ...kickerSx, mb: 1.5 }}
        >
          Want more entries?
        </Typography>

        <Typography
          id="giveaway-offer-more-entries-headline"
          sx={{
            fontSize: { xs: 26, sm: 30 },
            fontWeight: 900,
            lineHeight: 1.12,
            color: INK
          }}
        >
          Get up to {upTo.toLocaleString('en-AU')} more entries
        </Typography>

        <Button
          onClick={seePacks}
          sx={{ ...ctaSx, mt: { xs: 3, sm: 3.5 }, width: '100%' }}
        >
          See the packs
        </Button>
      </Box>
    </Dialog>
  );
};

export default GiveawayOfferMoreEntriesDialog;
